import type { ClaimPolymorph } from '~/components/types/claim-types';
import { isInvolvedNonMotorist } from '~/components/types/claim-types';
import { policyPopulate } from '~/PolicyUtils';
import type {
  Claim,
  ClaimForFe,
  GenericPropertyParty,
  InvolvedPerson,
  InvolvedPersonParty,
  InvolvedProperty,
  NonMotoristParty,
  SubOrganizationModel,
  VehicleParty,
} from '~/server_shared/swagger/data-contracts';
import { reportErrorInProductionOrThrow } from '~/Utils';

export type ClaimInput = Claim | ClaimForFe;

// The return type of populateClaim augments ClaimPolymorph with runtime-populated fields
export type PopulatedClaim = ClaimPolymorph & {
  incident?: ClaimPolymorph['incident'] & IncidentPartiesProperties;
  policy: ClaimPolymorph['policy'] & {
    // Added by policyPopulate in FE
    insured_address?: string;
    // Added in ClaimContextProvider for sub-organization convenience
    sub_organization?: SubOrganizationModel;
  };
};

// Type for the properties that buildClaimPartiesFromInvolvedEntities adds to incident
type IncidentPartiesProperties = {
  other_properties_parties?: GenericPropertyParty[];
  vehicle_parties?: VehicleParty[];
  non_motorist_parties?: NonMotoristParty[];
  involved_person_parties?: InvolvedPersonParty[];
};

// Builds parties mapping for efficient claim responses (load_type === 'fe')
function buildClaimPartiesFromInvolvedEntities(claim: ClaimPolymorph): IncidentPartiesProperties {
  if (claim.load_type !== 'fe') {
    return {};
  }

  const incident = claim.incident;

  const getInvolvedByIncidentPartyId = (list: InvolvedProperty[], id: number) =>
    list.find((x) => x.incident_party_id === id);
  const getInvolvedByIncidentPartyIdAndType = (list: InvolvedPerson[], id: number, type: string) =>
    list.find((x) => x.incident_party_id === id && x.type === type);

  if (claim.type === 'auto_claim') {
    const other_properties_parties: GenericPropertyParty[] = [];
    const vehicle_parties: VehicleParty[] = [];
    const non_motorist_parties: NonMotoristParty[] = [];

    incident?.party_types?.forEach((partyType) => {
      switch (partyType.type) {
        case 'vehicle_party':
          vehicle_parties.push({
            ...partyType,
            involved_vehicle: getInvolvedByIncidentPartyId(incident?.involved_properties || [], partyType.id),
            driver: getInvolvedByIncidentPartyIdAndType(
              incident?.involved_persons || [],
              partyType.id,
              'involved_driver'
            ),
            passengers: incident?.involved_persons?.filter(
              (x) => x.incident_party_id === partyType.id && x.type === 'involved_passenger'
            ),
          });
          break;
        case 'non_motorist_party': {
          const involvedNonMotorist = getInvolvedByIncidentPartyIdAndType(
            incident?.involved_persons || [],
            partyType.id,
            'involved_non_motorist'
          );

          // Validate that the involved person is actually a non-motorist with the required field
          if (involvedNonMotorist && !isInvolvedNonMotorist(involvedNonMotorist)) {
            reportErrorInProductionOrThrow(
              `Expected involved_non_motorist to have non_motorist_type, but got ${JSON.stringify(involvedNonMotorist)}`
            );
          }

          non_motorist_parties.push({
            ...partyType,
            involved_non_motorist: involvedNonMotorist,
            non_motorist_type: isInvolvedNonMotorist(involvedNonMotorist)
              ? involvedNonMotorist.non_motorist_type
              : undefined,
          });
          break;
        }
        case 'generic_property_party':
          other_properties_parties.push({
            ...partyType,
            generic_property_involved: getInvolvedByIncidentPartyId(incident.involved_properties || [], partyType.id),
          });
          break;
        default:
          throw new Error(`Unknown incident party type ${partyType.type}`);
      }
    });

    return { other_properties_parties, vehicle_parties, non_motorist_parties };
  }

  if (claim.type === 'general_claim') {
    const other_properties_parties: GenericPropertyParty[] = [];
    const involved_person_parties: InvolvedPersonParty[] = [];

    incident?.party_types?.forEach((partyType) => {
      switch (partyType.type) {
        case 'generic_property_party':
          other_properties_parties.push({
            ...partyType,
            generic_property_involved: getInvolvedByIncidentPartyId(incident.involved_properties || [], partyType.id),
          });
          break;
        case 'involved_person_party':
          involved_person_parties.push({
            ...partyType,
            involved_person: getInvolvedByIncidentPartyId(incident.involved_persons || [], partyType.id),
          });
          break;
        default:
          throw new Error(`Unknown incident party type ${partyType.type}`);
      }
    });
    return { other_properties_parties, involved_person_parties };
  }

  return {};
}

export function populateClaim(claim: ClaimPolymorph): PopulatedClaim {
  return {
    ...claim,
    incident: {
      ...(claim.incident || {}),
      ...buildClaimPartiesFromInvolvedEntities(claim),
    } as PopulatedClaim['incident'],
    policy: policyPopulate(claim.policy),
  };
}
